/**
 * Lanes tab scenarios from the chosen artboard (docs/ux/artboards/project-tab/lanes.html): the
 * same Viko project busy / needing you / all green, plus empty, idle-capacity, degraded and locked.
 * Used by the `lanes-model` unit tests and the mock-only `/dev/lanes` gallery (screenshots).
 */
import type { LanesInput, LivePhase } from '../lib/lanes-model.js'
import type {
  BuildCard,
  InboxItem,
  Landing,
  Machine,
  Project,
  RunSummary,
  TaskDetail,
  WorkstreamDetail,
} from '../lib/types.js'

export type LanesScenario = 'busy' | 'needs' | 'green' | 'empty' | 'idle' | 'degraded' | 'locked'
export const LANES_SCENARIOS: readonly LanesScenario[] = [
  'busy',
  'needs',
  'green',
  'empty',
  'idle',
  'degraded',
  'locked',
]

/** 28 Sep 2026, 15:00 local. */
export const LANES_NOW = new Date(2026, 8, 28, 15, 0).getTime()
const min = 60_000
const ago = (m: number) => LANES_NOW - m * min

export const LANES_PROJECTS: Project[] = [
  {
    id: 'p-viko',
    name: 'Viko',
    repo: 'arek/viko',
    machines: ['m-mac', 'm-photon'],
    loop: 'fast',
    workstreams: [],
    status: 'active',
    tab: { order: 0 },
    // The Viko repo's own lanes (viko.project.yaml) plus a gated public release.
    config: {
      version: 1,
      project: { id: 'p-viko', name: 'Viko' },
      repos: [],
      machines: { allowed: ['mac', 'photon'] },
      loopProfile: 'fast',
      autonomy: { level: 'balanced', overrides: { 'land-main': 'auto', 'ship-internal': 'ask' } },
      releaseLanes: [
        {
          id: 'web-staging',
          name: 'Staging test build',
          target: 'web-cloudflare',
          machine: 'mac',
          trigger: { kind: 'on-land' },
          production: false,
        },
        {
          id: 'daemon-pilot',
          name: 'Internal Mac daemon build',
          target: 'custom',
          machine: 'mac',
          trigger: { kind: 'manual' },
          production: false,
        },
        {
          id: 'public',
          name: 'Public release',
          target: 'web-cloudflare',
          machine: 'mac',
          trigger: { kind: 'manual' },
          production: true,
        },
      ],
    },
  },
  {
    id: 'p-telltale',
    name: 'Telltale',
    repo: 'arek/telltale',
    machines: ['m-photon'],
    loop: 'careful',
    workstreams: [],
    status: 'active',
    tab: { order: 1 },
  },
  {
    id: 'p-zip',
    name: 'Zip Mail',
    repo: 'arek/zipmail',
    machines: ['m-mac'],
    loop: 'fast',
    workstreams: [],
    status: 'active',
    tab: { order: 2 },
  },
]

const machine = (id: string, name: string, online: boolean, running = 0): Machine => ({
  id,
  name,
  platform: id === 'm-mac' ? 'mac' : 'windows',
  last_seen_at: LANES_NOW,
  online,
  daemons: [],
  caps: { maxRuns: 4, running },
})

const ws = (id: string, name: string, extra: Partial<WorkstreamDetail> = {}): WorkstreamDetail => ({
  id,
  projectId: 'p-viko',
  name,
  branch: `viko/${id}`,
  worktree: null,
  machineId: null,
  brief: null,
  status: 'active',
  ideaId: null,
  liveRun: null,
  bulkApproved: false,
  paused: false,
  agent: null,
  counts: { open: 0, proposed: 0, done: 0 },
  createdAt: ago(3000),
  updatedAt: ago(10),
  ...extra,
})

const task = (
  id: string,
  workstreamId: string,
  title: string,
  status: TaskDetail['status'],
  extra: Partial<TaskDetail> = {},
): TaskDetail => ({
  id,
  projectId: 'p-viko',
  workstreamId,
  title,
  description: null,
  status,
  labels: [],
  deps: [],
  acceptance: [],
  provenance: 'user',
  proposed: status === 'proposed',
  priority: 5,
  agentHint: null,
  requires: [],
  rationale: null,
  progress: null,
  blockedReason: null,
  runId: null,
  ideaId: null,
  notBefore: null,
  parked: null,
  landedAt: null,
  createdAt: ago(600),
  updatedAt: ago(20),
  ...extra,
})

const run = (
  runId: string,
  taskId: string,
  workstreamId: string,
  machineId: string,
  agent: 'claude' | 'codex',
  status: string,
  extra: Partial<RunSummary> = {},
): RunSummary => ({
  runId,
  machineId,
  agent,
  status,
  headSeq: 100,
  updatedAt: ago(1),
  startedAt: ago(90),
  projectId: 'p-viko',
  projectName: 'Viko',
  workstreamId,
  taskId,
  ...extra,
})

const question = (
  id: string,
  runId: string,
  header: string,
  q: string,
  options: string[],
  extra: Partial<InboxItem> = {},
): InboxItem => ({
  id,
  runId,
  type: 'question',
  seq: 42,
  title: header,
  blocking: true,
  createdAt: ago(120),
  score: 9,
  projectName: 'Viko',
  machineId: 'm-photon',
  event: {
    v: 0,
    runId,
    seq: 42,
    eventId: `ev-${id}`,
    ts: ago(120),
    agent: 'claude',
    kind: 'question',
    questionId: id,
    questions: [
      {
        id: 'q1',
        header,
        question: q,
        options: options.map((label) => ({ label })),
        multiSelect: false,
        allowOther: true,
      },
    ],
  } as InboxItem['event'],
  ...extra,
})

const landed = (id: string, taskId: string, commit: string, at: number): Landing => ({
  id,
  projectId: 'p-viko',
  workstreamId: null,
  runId: null,
  branch: `viko/${taskId}`,
  state: 'landed',
  step: null,
  steps: [],
  taskIds: [taskId],
  commit,
  summary: null,
  error: null,
  machineId: 'm-mac',
  createdAt: at - 5 * min,
  updatedAt: at,
})

/** An earlier landing attempt for a branch that later landed (collapsed in the strip). */
const attempt = (id: string, taskId: string, reason: string, at: number): Landing => ({
  ...landed(id, taskId, '', at),
  taskIds: [],
  state: 'failed',
  commit: null,
  reason,
})

const build = (
  buildId: string,
  laneId: string,
  title: string,
  version: string,
  subtitle: string,
  status: string,
  at: number,
  production = false,
): BuildCard => ({
  buildId,
  projectId: 'p-viko',
  laneId,
  title,
  subtitle,
  status,
  detail: null,
  version,
  production,
  tasks: [],
  taskIds: [],
  links: [],
  failure: null,
  actions: [],
  createdAt: at,
  updatedAt: at,
})

type St = 'busy' | 'needs' | 'green'

function vikoInput(st: St): Omit<LanesInput, 'now'> {
  const pick = <T>(v: Record<St, T>) => v[st]
  const workstreams = [
    ws('setup', 'Setup and CLI', { rank: 0 }),
    ws('accounts', 'Accounts', { rank: 1 }),
    ws('viewer', 'Conversation viewer', { rank: 2 }),
    ws('ci', 'CI and builds', { rank: 3, machineId: 'm-photon' }),
  ]
  const tasks: TaskDetail[] = [
    task('cli', 'setup', 'CLI UX refresh', 'done', { landedAt: ago(200) }),
    task(
      'passkeys',
      'setup',
      'Fewer passkey prompts',
      pick({ busy: 'running', needs: 'running', green: 'done' }),
      {
        runId: 'r-passkeys',
        ...(st === 'green' ? { landedAt: ago(40) } : {}),
      },
    ),
    task('installer', 'setup', 'Installer update check', 'ready', { provenance: 'agent' }),
    task(
      'accountproj',
      'accounts',
      'Account projects',
      pick({ busy: 'running', needs: 'running', green: 'done' }),
      { runId: 'r-accounts', ...(st === 'green' ? { landedAt: ago(30) } : {}) },
    ),
    task(
      'kit',
      'accounts',
      'Recovery kit',
      pick({ busy: 'running', needs: 'running', green: 'done' }),
      { runId: 'r-kit', ...(st === 'green' ? { landedAt: ago(25) } : {}) },
    ),
    task(
      'perproj',
      'accounts',
      'Per-project default account',
      pick({ busy: 'ready', needs: 'ready', green: 'review' }),
      { barkIds: ['b2'] },
    ),
    task(
      'migrate',
      'accounts',
      'Migrate the existing global account',
      pick({ busy: 'ready', needs: 'ready', green: 'done' }),
      { barkIds: ['b2'], ...(st === 'green' ? { landedAt: ago(15) } : {}) },
    ),
    task(
      'transcript',
      'viewer',
      'Transcript polish',
      pick({ busy: 'running', needs: 'running', green: 'done' }),
      { runId: 'r-transcript', ...(st === 'green' ? { landedAt: ago(20) } : {}) },
    ),
    task('search', 'viewer', 'Search inside a transcript', 'ready', { provenance: 'agent' }),
    task('e2e', 'ci', 'e2e:int fix', 'done', { landedAt: ago(170) }),
    task('nightly', 'ci', 'Nightly fixture refresh', 'ready', { provenance: 'agent' }),
    task('old', 'ci', 'Photon runner image', 'done', { landedAt: ago(60 * 30) }),
  ]
  const runs: RunSummary[] =
    st === 'green'
      ? []
      : [
          run(
            'r-passkeys',
            'passkeys',
            'setup',
            'm-mac',
            'claude',
            pick({ busy: 'running', needs: 'waiting', green: 'idle' }),
            {
              title: 'Fewer passkey prompts',
              activity:
                'Replacing per-landing passkey prompts with a landing grant in apps/control/src/auth/grants.ts',
              ...(st === 'needs' ? { blockedOn: 'How long may a landing grant last?' } : {}),
            },
          ),
          run('r-accounts', 'accountproj', 'accounts', 'm-photon', 'codex', 'running', {
            title: 'Account projects',
            activity: pick({
              busy: 'Running the account-switch e2e suite: 41 of 58 passing so far',
              needs: 'Fixing the session handoff when you switch accounts mid-run',
              green: '',
            }),
          }),
          run(
            'r-kit',
            'kit',
            'accounts',
            'm-photon',
            'claude',
            pick({ busy: 'running', needs: 'waiting', green: 'idle' }),
            {
              title: 'Recovery kit',
              activity: 'Writing the kit renderer, printable layout first',
              ...(st === 'needs' ? { blockedOn: 'Printable sheet or encrypted file?' } : {}),
            },
          ),
          run('r-transcript', 'transcript', 'viewer', 'm-mac', 'claude', 'running', {
            title: 'Transcript polish',
            activity: pick({
              busy: 'Collapsing repeated tool calls; 3 of 5 fixtures updated',
              needs: 'Shrinking tool-call rows on mobile, as you asked 5 minutes ago',
              green: '',
            }),
          }),
        ]
  const inbox: InboxItem[] =
    st === 'needs'
      ? [
          question(
            'q-kit',
            'r-kit',
            'Recovery kit format',
            'Recovery kit: a printable sheet or an encrypted file?',
            ['Printable sheet (Recommended)', 'Encrypted file', 'Both'],
            { unblocks: 3, score: 9, createdAt: ago(120) },
          ),
          question(
            'q-grant',
            'r-passkeys',
            'Landing grant',
            'Allow a landing grant so internal lanes land without a tap each time?',
            ['12 hours (Recommended)', '4 hours', 'Keep a tap per landing'],
            { unblocks: 2, score: 7, createdAt: ago(40) },
          ),
        ]
      : st === 'busy'
        ? [
            question(
              'q-digest',
              'r-transcript',
              'Weekly digest',
              'Weekly digest of what landed: after V1, or now as a small task?',
              ['After V1', 'Now, in CI and builds'],
              { blocking: false, score: 1, createdAt: ago(120) },
            ),
          ]
        : []
  const landings: Landing[] = [
    // e2e landed on its fourth attempt: the strip shows it landed, the attempts in its inspector.
    attempt('l-e2e-1', 'e2e', 'Rebase conflict in apps/web/src/lib/lanes-model.ts', ago(260)),
    attempt('l-e2e-2', 'e2e', 'pnpm verify failed (exit 1)', ago(230)),
    attempt('l-e2e-3', 'e2e', 'pnpm e2e:int fails on main too', ago(190)),
    landed('l-cli', 'cli', '8c1e4a2f', ago(200)),
    landed('l-e2e', 'e2e', '5f09c3d1', ago(170)),
    // landing-modes: a stack (two children into feature/global-integ) and a conflict task wait.
    ...(st === 'needs'
      ? [
          {
            ...landed('l-g1', 'global-a', '3b7d9e01', ago(900)),
            targetBranch: 'feature/global-integ',
            gate: { tiers: ['docs'], strictness: 0, files: 2, commands: ['pnpm lint:docs'] },
            recipe: [{ name: 'recipe 1', command: 'pnpm lint:docs', status: 'reused' as const }],
          },
          {
            ...attempt('l-g2', 'global-b', 'rebase conflict in src/core.ts', ago(30)),
            targetBranch: 'feature/global-integ',
            waitingOn: {
              kind: 'conflict-task' as const,
              taskId: 't-conflict-1',
              title: 'Resolve landing conflict: viko/global-b vs feature/global-integ',
            },
            failure: {
              phase: 'rebase',
              phaseLabel: 'waiting on conflict task',
              message: 'Waiting on conflict task: rebase conflicts in src/core.ts',
              files: ['src/core.ts'],
              nextAction:
                'The agent resolves the conflict; Viko lands again once the branch changes.',
            },
            gate: {
              tiers: ['default', 'core'],
              strictness: 80,
              files: 5,
              commands: ['pnpm verify', 'pnpm audit:core'],
            },
            recipe: [
              { name: 'recipe 1', command: 'pnpm verify', status: 'pass' as const },
              { name: 'recipe 2', command: 'pnpm audit:core', status: 'pending' as const },
            ],
          },
          {
            ...attempt('l-gi', 'global-integ', '', ago(10)),
            branch: 'feature/global-integ',
            state: 'queued',
            reason: undefined,
            targetBranch: 'master',
          },
        ]
      : []),
    ...(st === 'busy'
      ? [
          { ...attempt('l-kit-run', 'kit', '', ago(1)), state: 'running', step: 'testing' },
          { ...attempt('l-tr-q', 'transcript', '', ago(0)), state: 'queued' },
        ]
      : []),
    ...(st === 'green'
      ? [
          landed('l-pk', 'passkeys', 'e19b07d4', ago(40)),
          landed('l-ap', 'accountproj', '4ad2f901', ago(30)),
          landed('l-kit', 'kit', 'b7730c12', ago(25)),
          landed('l-tr', 'transcript', 'd2f5a88e', ago(20)),
          landed('l-mg', 'migrate', '0c4e61b3', ago(15)),
        ]
      : []),
  ]
  const builds = [
    {
      ...build(
        'b-tb',
        'web-staging',
        `Build ${st === 'green' ? '2026.09.28-4' : '2026.09.28-3'} on Staging test build`,
        st === 'green' ? '2026.09.28-4' : '2026.09.28-3',
        'on staging.viko.sh',
        'available',
        ago(st === 'green' ? 6 : 180),
      ),
      links: [{ label: 'Staging', url: 'https://staging.viko.sh/' }],
      // release-lanes-v2: the lane verify proved this commit live on staging.
      headCommit: '5f09c3d1e2a4',
      tasks: [
        { taskId: 'cli', title: 'CLI UX refresh', workstreamId: 'setup' },
        { taskId: 'e2e', title: 'e2e:int fix', workstreamId: 'ci' },
      ],
      taskIds: ['cli', 'e2e'],
      live: {
        environment: 'staging',
        version: st === 'green' ? '2026.09.28-4' : '2026.09.28-3',
        commit: '5f09c3d1e2a4',
        since: ago(165),
        verifiedAt: ago(st === 'green' ? 5 : 165),
        source: 'verify',
        detail: 'staging.viko.sh serves 5f09c3d',
        url: 'https://staging.viko.sh/version',
      },
    },
    build(
      'b-dl',
      'daemon-pilot',
      'Build 0.4.12 on Internal Mac daemon build',
      '0.4.12',
      'Photon and Mac updated',
      'available',
      ago(st === 'green' ? 12 : 240),
    ),
    build(
      'b-pr',
      'public',
      'Build 0.4 on Public release',
      '0.4',
      'waits for a passkey tap',
      'queued',
      ago(30),
      true,
    ),
  ]
  const live = new Map<string, LivePhase>(
    st === 'green'
      ? []
      : [
          [
            'r-accounts',
            {
              phase: 'making-progress',
              label: 'Making progress',
              evidence: pick({
                busy: '41 of 58 e2e passing',
                needs: '52 of 58 e2e passing',
                green: '',
              }),
              line: runs[1]?.activity ?? '',
              streaming: true,
            },
          ],
          [
            'r-transcript',
            {
              phase: 'making-progress',
              label: 'Making progress',
              evidence: 'Fixture replays: 3 of 5 match',
              line: runs[3]?.activity ?? '',
              streaming: true,
            },
          ],
          ...(st === 'busy'
            ? ([
                [
                  'r-passkeys',
                  {
                    phase: 'working',
                    label: 'Working',
                    evidence: '38 tests passing so far',
                    line: runs[0]?.activity ?? '',
                    streaming: true,
                  },
                ],
                [
                  'r-kit',
                  {
                    phase: 'working',
                    label: 'Working',
                    evidence: '12 tests passing',
                    line: runs[2]?.activity ?? '',
                    streaming: true,
                  },
                ],
              ] as [string, LivePhase][])
            : []),
        ],
  )
  return {
    project: LANES_PROJECTS[0] as Project,
    workstreams,
    tasks,
    runs,
    inbox,
    landings,
    builds,
    machines: [
      {
        ...machine('m-mac', 'Mac', true, 2),
        // ship-internal: ask — the staging lane waits for a tap after e2e landed.
        ...(st === 'needs'
          ? {
              pendingShips: [
                { projectId: 'p-viko', laneId: 'web-staging', gate: 'tap', landings: 1 },
              ],
            }
          : {}),
      },
      machine('m-photon', 'Photon', true, st === 'green' ? 0 : 2),
    ],
    live,
    connection: 'live',
  }
}

export function lanesScenario(s: LanesScenario): LanesInput {
  const now = LANES_NOW
  if (s === 'empty')
    return {
      project: LANES_PROJECTS[2] as Project,
      workstreams: [],
      tasks: [],
      runs: [],
      inbox: [],
      landings: [],
      builds: [],
      machines: [machine('m-mac', 'Mac', true)],
      connection: 'live',
      now,
    }
  if (s === 'idle') {
    const g = vikoInput('green')
    // Nothing landed today yet; queued follow-ups wait for a free machine.
    return {
      ...g,
      tasks: g.tasks.map((t) =>
        t.status === 'done' && t.landedAt && t.landedAt > ago(600)
          ? { ...t, landedAt: ago(60 * 26) }
          : t.status === 'review'
            ? { ...t, status: 'ready' as const }
            : t,
      ),
      builds: g.builds.map((b) => ({ ...b, updatedAt: ago(60 * 26) })),
      now,
    }
  }
  if (s === 'degraded') {
    const b = vikoInput('busy')
    return {
      ...b,
      machines: [machine('m-mac', 'Mac', true, 2), machine('m-photon', 'Photon', false)],
      connection: 'offline',
      now,
    }
  }
  if (s === 'locked') return { ...vikoInput('busy'), locked: true, now }
  return { ...vikoInput(s), now }
}
